// B-154 — the pure half of Banco Agrario's "Transacciones y reversas" (Reportes) and of a closing's
// reversals. The backend sums everything (`getCierreTransactionsDashboard`); this only chooses the
// period, words the axis and lays the rows out for the charts and the Excel.

import divipola from "../data/municipiosColombia.json";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const hoyBogota = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Bogota" });

/** The period chips: from the client's start (the backend's default), this year, the last 12 months, this month. */
export const PERIODOS = [
  { clave: "todo", etiqueta: "Desde 2024" },
  { clave: "anio", etiqueta: "Este año" },
  { clave: "doce", etiqueta: "12 meses" },
  { clave: "mes", etiqueta: "Este mes" },
];

/**
 * B-161 — one chip per year since the dashboard starts, as the operator's own dashboard filters by year:
 * `{ clave: "anio-2025", etiqueta: "2025" }`, newest first.
 */
export function aniosDelTablero(desde, hoy = hoyBogota()) {
  const primero = Number(String(desde || "").slice(0, 4)) || Number(hoy.slice(0, 4));
  const ultimo = Number(hoy.slice(0, 4));
  const anios = [];
  for (let y = ultimo; y >= primero; y -= 1) anios.push({ clave: `anio-${y}`, etiqueta: String(y) });
  return anios;
}

/** `{ from, to }` for a chip — `{}` for "todo", which leaves both to the backend. */
export function rangoDePeriodo(clave, hoy = hoyBogota()) {
  const [y, m] = hoy.split("-").map(Number);
  const anio = /^anio-(\d{4})$/.exec(clave || "");
  if (anio) {
    const fin = `${anio[1]}-12-31`;
    return { from: `${anio[1]}-01-01`, to: fin < hoy ? fin : hoy };
  }
  if (clave === "anio") return { from: `${y}-01-01`, to: hoy };
  if (clave === "mes") return { from: `${hoy.slice(0, 7)}-01`, to: hoy };
  if (clave === "doce") {
    const inicio = new Date(Date.UTC(y, m - 12, 1));
    return { from: inicio.toISOString().slice(0, 10), to: hoy };
  }
  return {};
}

/** A bucket on the axis: `2026-10` → `oct 26`, `2026-10-05` → `05 oct`. */
export function etiquetaDeBucket(bucket) {
  const [y, m, d] = String(bucket || "").split("-");
  if (d) return `${d} ${MESES[Number(m) - 1] || m}`;
  return `${MESES[Number(m) - 1] || m} ${String(y).slice(2)}`;
}

/** The series for the chart: one row per bucket, the `top` concepts by value and the rest as "otras". */
export function puntosDeSerie(datos, top = 5) {
  const principales = (datos?.byConcept || []).slice(0, top).map((c) => c.concept);
  return (datos?.series || []).map((punto) => {
    // B-161 — a calendar day with no transactions (a weekend, a holiday) is blank, never a zero.
    if (punto.empty) {
      return {
        bucket: punto.bucket, etiqueta: etiquetaDeBucket(punto.bucket), tx: null, valor: null, reversas: null, otras: null, vacio: true,
      };
    }
    const fila = {
      bucket: punto.bucket, etiqueta: etiquetaDeBucket(punto.bucket), tx: punto.tx, valor: punto.valor, reversas: punto.reversals, otras: 0,
    };
    Object.entries(punto.concepts || {}).forEach(([concepto, cifras]) => {
      if (principales.includes(concepto)) fila[concepto] = cifras.tx;
      else fila.otras += cifras.tx;
    });
    return fila;
  });
}

/** A variation as the screen says it: "+12,5 %", "−3 %", "—" when there is nothing to compare. */
export function variacionEnPalabras(v) {
  if (v === null || v === undefined) return "—";
  const texto = `${Math.abs(v).toLocaleString("es-CO", { maximumFractionDigits: 1 })} %`;
  if (v > 0) return `+${texto}`;
  if (v < 0) return `−${texto}`;
  return texto;
}

/** `2026-10` → "oct 2026". */
export const mesEnPalabras = (mes) => {
  const [y, m] = String(mes || "").split("-");
  return `${MESES[Number(m) - 1] || m} ${y}`;
};

/**
 * B-161 — a comparison's lines for its chart and table, in one measure (`tx` or `valor`): this period's
 * figure, the one before, the daily variation and the accumulated one. A line still to come keeps the
 * period before and nothing of its own.
 */
export function filasDeComparacion(lineas, medida, etiqueta = (clave) => String(clave)) {
  return (lineas || []).map((l) => ({
    clave: l.key,
    etiqueta: etiqueta(l.key),
    actual: l.current ? l.current[medida] : null,
    anterior: l.previous ? l.previous[medida] : null,
    variacion: l.variation ? l.variation[medida] : null,
    acumuladoActual: l.accumulated ? l.accumulated.current[medida] : null,
    acumuladoAnterior: l.accumulated ? l.accumulated.previous[medida] : null,
    variacionAcumulada: l.accumulated ? l.accumulated.variation[medida] : null,
  }));
}

/** The last line of a comparison that has an accumulated figure: where the period stands today. */
export const acumuladoAlCorte = (lineas) => [...(lineas || [])].reverse().find((l) => l.accumulated) || null;

/**
 * B-171 — a municipality's name from its DANE code, as DIVIPOLA writes it ("CÁCERES"); the code itself
 * when DIVIPOLA does not have it, and the backend's "Sin municipio" as it came.
 */
export function nombreDeMunicipio(dane) {
  const fila = divipola.municipios && divipola.municipios[dane];
  return fila ? fila[2] : dane;
}

/** The municipalities a select offers: every one, or only those of the department chosen. */
export const municipiosDe = (municipios, departamento) => (municipios || [])
  .filter((m) => !departamento || m.department === departamento)
  .map((m) => ({ ...m, nombre: nombreDeMunicipio(m.dane) }))
  .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));

/** Reversals per 1.000 transactions, in words: "0,5 por cada 1.000" — or "—" when nothing to compare. */
export const tasaEnPalabras = (tasa) => (tasa === null || tasa === undefined
  ? "—"
  : `${String(tasa).replace(".", ",")} por cada 1.000`);

/** What "Exportar Excel" writes: the months, the concepts, the regions, the offices and the reversals. */
export function hojasDeTransacciones(datos) {
  const conceptos = datos?.concepts || [];
  return [
    {
      nombre: "Por periodo",
      titulo: "Transacciones y reversas por periodo",
      columnas: [
        { titulo: "Periodo", ancho: 12 },
        { titulo: "Transacciones", formato: "count" },
        { titulo: "Valor", formato: "money", ancho: 20 },
        ...conceptos.map((c) => ({ titulo: `${c.label} (TX)`, formato: "count" })),
        { titulo: "Reversas", formato: "count" },
        { titulo: "Valor reversas", formato: "money", ancho: 18 },
      ],
      filas: (datos?.series || []).map((p) => [
        p.bucket, p.tx, p.valor, ...conceptos.map((c) => (p.concepts?.[c.key]?.tx) || 0), p.reversals, p.reversalsValue,
      ]),
    },
    {
      nombre: "Por transacción",
      columnas: [{ titulo: "Transacción", ancho: 28 }, { titulo: "Transacciones", formato: "count" }, { titulo: "Valor", formato: "money", ancho: 20 }],
      filas: (datos?.byConcept || []).map((c) => [c.label, c.tx, c.valor]),
    },
    {
      nombre: "Por regional",
      columnas: [
        { titulo: "Regional", ancho: 18 }, { titulo: "Oficinas", formato: "count" }, { titulo: "Transacciones", formato: "count" },
        { titulo: "Valor", formato: "money", ancho: 20 }, { titulo: "Reversas", formato: "count" }, { titulo: "Valor reversas", formato: "money", ancho: 18 },
        { titulo: "Reversas por 1.000 TX" },
      ],
      filas: (datos?.byRegion || []).map((r) => [r.region, r.offices, r.tx, r.valor, r.reversals, r.reversalsValue, r.rate ?? ""]),
    },
    {
      nombre: "Por departamento",
      columnas: [
        { titulo: "Departamento", ancho: 20 }, { titulo: "Oficinas", formato: "count" }, { titulo: "Transacciones", formato: "count" },
        { titulo: "Valor", formato: "money", ancho: 20 }, { titulo: "Reversas", formato: "count" }, { titulo: "Valor reversas", formato: "money", ancho: 18 },
        { titulo: "Reversas por 1.000 TX" },
      ],
      filas: (datos?.byDepartment || []).map((d) => [d.department, d.offices, d.tx, d.valor, d.reversals, d.reversalsValue, d.rate ?? ""]),
    },
    {
      nombre: "Por municipio",
      columnas: [
        { titulo: "DANE" }, { titulo: "Municipio", ancho: 22 }, { titulo: "Departamento", ancho: 18 }, { titulo: "Oficinas", formato: "count" },
        { titulo: "Transacciones", formato: "count" }, { titulo: "Valor", formato: "money", ancho: 20 }, { titulo: "Reversas", formato: "count" },
        { titulo: "Valor reversas", formato: "money", ancho: 18 }, { titulo: "Reversas por 1.000 TX" },
      ],
      filas: (datos?.byMunicipality || []).map((m) => [
        m.dane, nombreDeMunicipio(m.dane), m.department, m.offices, m.tx, m.valor, m.reversals, m.reversalsValue, m.rate ?? "",
      ]),
    },
    {
      nombre: "Por oficina",
      columnas: [
        { titulo: "Código" }, { titulo: "Oficina", ancho: 24 }, { titulo: "Regional", ancho: 14 }, { titulo: "Transacciones", formato: "count" },
        { titulo: "Valor", formato: "money", ancho: 20 }, { titulo: "Reversas", formato: "count" }, { titulo: "Valor reversas", formato: "money", ancho: 18 },
        { titulo: "Reversas por 1.000 TX" },
      ],
      filas: (datos?.byOffice || []).map((o) => [o.officeCode, o.officeName, o.region, o.tx, o.valor, o.reversals, o.reversalsValue, o.rate ?? ""]),
    },
    {
      nombre: "Reversas",
      columnas: [
        { titulo: "Fecha", ancho: 12 }, { titulo: "Código" }, { titulo: "Oficina", ancho: 22 }, { titulo: "Cajero", ancho: 22 },
        { titulo: "Transacción", ancho: 22 }, { titulo: "Motivo", ancho: 32 }, { titulo: "Atribución", ancho: 12 }, { titulo: "Valor", formato: "money", ancho: 16 },
      ],
      filas: (datos?.reversals?.latest || []).map((r) => [r.transactionDate, r.officeCode, r.officeName, r.cashier || "", r.transactionType, r.motive, r.attribution, r.amount]),
    },
    ...hojasDeComparacion(datos?.comparisons),
  ];
}

/** B-161 — the two comparisons as sheets: each line in transactions and in value, daily and accumulated. */
function hojasDeComparacion(comparaciones) {
  if (!comparaciones) return [];
  const columnas = (actual, anterior) => [
    { titulo: "", ancho: 8 },
    { titulo: `TX ${actual}`, formato: "count" }, { titulo: `TX ${anterior}`, formato: "count" }, { titulo: "Var. TX %" },
    { titulo: `TX acumuladas ${actual}`, formato: "count" }, { titulo: `TX acumuladas ${anterior}`, formato: "count" }, { titulo: "Var. acumulada TX %" },
    { titulo: `Valor ${actual}`, formato: "money", ancho: 18 }, { titulo: `Valor ${anterior}`, formato: "money", ancho: 18 }, { titulo: "Var. valor %" },
    { titulo: `Valor acumulado ${actual}`, formato: "money", ancho: 18 }, { titulo: `Valor acumulado ${anterior}`, formato: "money", ancho: 18 },
    { titulo: "Var. acumulada valor %" },
  ];
  const vacio = (v) => (v === null || v === undefined ? "" : v);
  const filas = (lineas) => lineas.map((l) => [
    l.key,
    vacio(l.current?.tx), vacio(l.previous?.tx), vacio(l.variation?.tx),
    vacio(l.accumulated?.current.tx), vacio(l.accumulated?.previous.tx), vacio(l.accumulated?.variation.tx),
    vacio(l.current?.valor), vacio(l.previous?.valor), vacio(l.variation?.valor),
    vacio(l.accumulated?.current.valor), vacio(l.accumulated?.previous.valor), vacio(l.accumulated?.variation.valor),
  ]);
  const { months, years } = comparaciones;
  return [
    {
      nombre: "Comparativo mensual",
      titulo: `${mesEnPalabras(months.current)} contra ${mesEnPalabras(months.previous)}, por día`,
      columnas: columnas(mesEnPalabras(months.current), mesEnPalabras(months.previous)).map((c, i) => (i ? c : { ...c, titulo: "Día" })),
      filas: filas(months.days),
    },
    {
      nombre: "Comparativo anual",
      titulo: `${years.current} contra ${years.previous}, por mes`,
      columnas: columnas(String(years.current), String(years.previous)).map((c, i) => (i ? c : { ...c, titulo: "Mes" })),
      filas: filas(years.months).map((fila) => [MESES[fila[0] - 1], ...fila.slice(1)]),
    },
  ];
}

/** "1.500.000" or "1500000,50" as the person types it → "1500000.50" as the backend takes it; null if not a number. */
export function valorParaEnviar(texto) {
  const limpio = String(texto || "").replace(/[$\s.]/g, "").replace(",", ".");
  return /^\d+(\.\d{1,2})?$/.test(limpio) && Number(limpio) > 0 ? limpio : null;
}
